'use client';

import { Button as GDDSButton, Tooltip } from '@geberit/gdds';
import { useState, useCallback, useMemo } from 'react';

// components
import {
  BUTTON_ALIGNMENT,
  BUTTON_BACKGROUND,
  BUTTON_COLORS,
  Button,
} from 'components/Button/Button';

// styles
import styles from './copy-to-clipboard.module.scss';

// utils
import { copyToClipboard } from 'utils/clipboard';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { useGdds } from 'utils/hooks/use-gdds';
import { classNameBuilder } from 'utils/classNameBuilder';

export function CopyToClipboard({
  text,
  isIcon = false,
}: Readonly<{ text?: string; isIcon?: boolean }>) {
  const translate = useTranslationFunction();
  const [copied, setCopied] = useState<boolean | undefined>();
  const [showTooltip, setShowTooltip] = useState(false);
  const isGdds = useGdds();

  const buttonText = useMemo(
    () => translate(copied ? 'web20_copied' : 'web20_copy'),
    [translate, copied],
  );

  const className = useMemo(() => copied && 'success', [copied]);

  const handleOnClick = useCallback(async () => {
    if (copied) return;

    if (!text) {
      setCopied(false);
      return;
    }

    try {
      await copyToClipboard(text);
      setCopied(true);

      if (isIcon) {
        setShowTooltip(true);

        setTimeout(() => {
          setShowTooltip(false);
        }, 2000);
      }

      setTimeout(() => {
        setCopied(undefined);
      }, 2000);
    } catch (error) {
      setCopied(false);
    }
  }, [copied, text, isIcon]);

  return isIcon ? (
    <Tooltip
      promptText={translate('web20_copied')}
      initialtooltipPosition="top"
      offset={{ top: 8 }}
      onClickedActive={showTooltip}
      active={showTooltip}
      onHoveredActive={false}
    >
      <GDDSButton symbol="link" stylingType="iconInverted" isIcon onClick={handleOnClick} />
    </Tooltip>
  ) : (
    <div className={styles.wrapper}>
      <p title={text}>{text}</p>
      {isGdds ? (
        <GDDSButton
          className={classNameBuilder(styles.nord, className && styles[className])}
          onClick={handleOnClick}
          stylingType="primary"
          symbol={copied ? 'checkmark' : undefined}
        >
          {buttonText}
        </GDDSButton>
      ) : (
        <Button
          color={BUTTON_COLORS.WHITE}
          background={BUTTON_BACKGROUND.PRIMARY}
          align={BUTTON_ALIGNMENT.CENTER}
          noTracking
          className={classNameBuilder(className && styles[className])}
          onClick={handleOnClick}
          symbol={copied ? 'check' : undefined}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
}
